// Applies legible Chart.js defaults for chartkick charts in dark mode.
//
// The app toggles a `.dark` class on <html>. We read that class and set
// `Chartkick.options` (grid / tick / legend colors), then redraw existing
// charts. Re-applied on DOMContentLoaded, turbo:load, a `darkmode:changed`
// custom event, and via a MutationObserver watching the <html> class attr.

const TICK_COLOR_DARK = "#9CA3AF" // gray-400
const TICK_COLOR_LIGHT = "#6B7280" // gray-500
const GRID_COLOR_DARK = "rgba(255, 255, 255, 0.08)"
const GRID_COLOR_LIGHT = "rgba(0, 0, 0, 0.06)"

function isDark() {
  return document.documentElement.classList.contains("dark")
}

// Locate the chartkick wrapper that owns a given Chart.js instance so we can
// read its options (prefix/suffix/etc.) inside Chart.js callbacks.
function chartkickOptionsFor(chartInstance) {
  let options = {}
  const ck = window.Chartkick
  if (ck && typeof ck.eachChart === "function") {
    ck.eachChart((c) => {
      const obj = typeof c.getChartObject === "function" ? c.getChartObject() : c.chart
      if (obj === chartInstance && typeof c.getOptions === "function") {
        options = c.getOptions() || {}
      }
    })
  }
  return options
}

// Mirror chartkick's value formatting (prefix/suffix/thousands/decimal) for the
// summed total. Rounds fractional totals to 2 places so JS float noise (e.g.
// 24.119999…) doesn't leak into "$" spend charts.
function formatChartTotal(value, options) {
  let pre = ""
  if (options.prefix) {
    if (value < 0) {
      value = -value
      pre = "-"
    }
    pre += options.prefix
  }

  let round = options.round
  if (round === undefined && value % 1 !== 0) round = 2

  let str = round === undefined ? String(value) : value.toFixed(round)

  if (options.thousands || options.decimal) {
    const parts = str.split(".")
    if (options.thousands) {
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, options.thousands)
    }
    str = parts.length > 1 ? parts[0] + (options.decimal || ".") + parts[1] : parts[0]
  }

  return pre + str + (options.suffix || "")
}

// Tooltip footer that sums every dataset shown at the hovered point. Only added
// for multi-series points (single-series charts don't need a redundant total).
function tooltipTotalFooter(items) {
  if (!items || items.length < 2) return ""

  // chartkick's `bar_chart` is horizontal (indexAxis: "y"), so its value sits on
  // the x axis; line/column/area charts keep the value on y.
  const horizontal = items[0].chart && items[0].chart.options.indexAxis === "y"
  const valueKey = horizontal ? "x" : "y"

  const sum = items.reduce((acc, item) => {
    const v = item && item.parsed ? item.parsed[valueKey] : null
    return acc + (typeof v === "number" ? v : 0)
  }, 0)

  return "Total: " + formatChartTotal(sum, chartkickOptionsFor(items[0].chart))
}

export function applyChartkickTheme() {
  if (typeof window === "undefined" || !window.Chartkick) return

  const dark = isDark()
  const tickColor = dark ? TICK_COLOR_DARK : TICK_COLOR_LIGHT
  const gridColor = dark ? GRID_COLOR_DARK : GRID_COLOR_LIGHT

  window.Chartkick.options = {
    library: {
      color: tickColor,
      borderColor: gridColor,
      plugins: {
        legend: {
          labels: { color: tickColor }
        },
        tooltip: {
          callbacks: { footer: tooltipTotalFooter }
        }
      },
      scales: {
        x: {
          ticks: { color: tickColor },
          grid: { color: gridColor }
        },
        y: {
          ticks: { color: tickColor },
          grid: { color: gridColor }
        }
      }
    }
  }

  // Redraw any already-rendered charts so the new theme takes effect.
  if (typeof window.Chartkick.eachChart === "function") {
    try {
      window.Chartkick.eachChart((chart) => {
        if (chart && typeof chart.redraw === "function") chart.redraw()
      })
    } catch (e) {
      // Defensive: never let theming break the page.
    }
  }
}

function setup() {
  applyChartkickTheme()

  // Re-apply whenever the .dark class on <html> changes (self-contained).
  if (typeof MutationObserver !== "undefined" && !setup.observed) {
    setup.observed = true
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "class") {
          applyChartkickTheme()
          break
        }
      }
    })
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"]
    })
  }
}

// Expose so the dark-mode toggle controller can call it directly.
if (typeof window !== "undefined") {
  window.applyChartkickTheme = applyChartkickTheme
}

document.addEventListener("DOMContentLoaded", setup)
document.addEventListener("turbo:load", applyChartkickTheme)
document.addEventListener("darkmode:changed", applyChartkickTheme)
